import classnames from 'classnames'

import Icon, { type IconName } from './Icon'

export type AlertTone = 'danger' | 'info' | 'neutral' | 'success' | 'warning'

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  icon?: React.ReactNode
  title?: React.ReactNode
  tone?: AlertTone
}

const toneClassMap: Record<AlertTone, string> = {
  danger: 'border-rose-200 bg-rose-50 text-rose-800',
  info: 'border-sky-200 bg-sky-50 text-sky-800',
  neutral: 'border-zinc-200 bg-zinc-50 text-zinc-700',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-800'
}

const defaultIconMap: Record<AlertTone, IconName> = {
  danger: 'alert',
  info: 'info',
  neutral: 'info',
  success: 'check-circle',
  warning: 'alert'
}

export default function Alert({
  children,
  className,
  icon,
  title,
  tone = 'neutral',
  ...props
}: AlertProps) {
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={classnames(
        'flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm',
        toneClassMap[tone],
        className
      )}
      {...props}
    >
      <span className="mt-0.5 shrink-0">
        {icon ?? <Icon name={defaultIconMap[tone]} />}
      </span>
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className={title ? 'mt-1' : ''}>{children}</div>
      </div>
    </div>
  )
}
